import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Briefcase, ArrowRight, ArrowLeft, CheckCircle, FloppyDisk } from "@phosphor-icons/react";
import BrainTypeProbabilities, { formatBrainTypeLabel } from "../components/BrainTypeProbabilities";
import CalendlyEmbed from "../components/CalendlyEmbed";

// Panksepp-letter mapping for the two "positive emotion" extras. Keeps Q94/Q95
// option lists mutually exclusive (last two Panksepp letters can never repeat).
const Q94_REWARD_LETTER = {
  "Kennis en zelfontwikkeling": "s",
  "Materiële beloningen zoals eten of geld": "l",
  "Sociale beloningen zoals bijvoorbeeld een compliment": "c",
  "Vrijheid en vrije tijd": "p",
};
const Q95_RELAX_LETTER = {
  "Iets uitzoeken en er dan achter komen hoe het zit": "s",
  "Spelen": "p",
  "Zorgen voor een ander": "c",
  "Lekker eten of geld verdienen": "l",
};

export default function SurveyPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [index, setIndex] = useState(0);
  const [likertAnswers, setLikertAnswers] = useState({});
  const [extraAnswers, setExtraAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [calendlyEmbed, setCalendlyEmbed] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [restored, setRestored] = useState(false);
  const saveTimer = useRef(null);
  const pendingPayload = useRef(null);
  const navigate = useNavigate();

  const flushSaveNow = async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const payload = pendingPayload.current;
    if (!payload) return;
    pendingPayload.current = null;
    try {
      await api.put("/survey/progress", payload);
      setSaveState("saved");
    } catch (err) {
      console.warn("Survey progress flush failed:", err?.message || err);
    }
  };

  const handleExit = async () => {
    await flushSaveNow();
    navigate("/tests");
  };

  useEffect(() => {
    api.get("/survey/questions").then((r) => {
      setData(r.data);
      const p = r.data.progress;
      if (p) {
        setLikertAnswers(p.likert_answers || {});
        setExtraAnswers(p.extra_answers || {});
        const total = (r.data.questions?.length || 0) + (r.data.extra_questions?.length || 0);
        const safeIdx = Math.min(Math.max(0, p.current_index || 0), Math.max(0, total - 1));
        setIndex(safeIdx);
        const hasAny = Object.keys(p.likert_answers || {}).length + Object.keys(p.extra_answers || {}).length > 0;
        if (hasAny) setRestored(true);
      }
    }).catch((e) => {
      const status = e.response?.status;
      if (status === 409) {
        navigate("/tests", { replace: true });
        return;
      }
      if (status === 403) {
        setError("De breintype-test is niet beschikbaar voor jouw account. Neem contact op met je work coach.");
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    });
  }, [navigate]);

  // Auto-save progress (debounced). Skip when data isn't loaded, when the test
  // is already finished (result set), or on the initial mount before the user
  // has touched anything.
  useEffect(() => {
    if (!data || result) return;
    const anyAnswers = Object.keys(likertAnswers).length + Object.keys(extraAnswers).length;
    if (anyAnswers === 0 && index === 0) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    const payload = {
      likert_answers: likertAnswers,
      extra_answers: extraAnswers,
      current_index: index,
    };
    pendingPayload.current = payload;
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put("/survey/progress", payload);
        setSaveState("saved");
      } catch (err) {
        console.warn("Survey progress save failed:", err?.message || err);
        setSaveState("idle");
      } finally {
        pendingPayload.current = null;
        saveTimer.current = null;
      }
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [data, result, likertAnswers, extraAnswers, index]);

  const allItems = useMemo(() => {
    if (!data) return [];
    return [
      ...data.questions.map((q) => ({ ...q, kind: "likert" })),
      ...data.extra_questions.map((q) => ({ ...q, kind: "choice" })),
    ];
  }, [data]);

  if (error) return <CenterMessage>{error}</CenterMessage>;
  if (!data) return <CenterMessage>Laden...</CenterMessage>;
  if (result) return <ResultScreen result={result} calendlyEmbed={calendlyEmbed} onClose={() => navigate("/tests")} />;

  const total = allItems.length;
  const current = allItems[index];
  const progress = ((index) / total) * 100;
  const isLikert = current.kind === "likert";
  const currentAnswer = isLikert ? likertAnswers[current.id] : extraAnswers[current.id];

  const hiddenLetter = current.id === 95
    ? Q94_REWARD_LETTER[extraAnswers[94]] || null
    : null;
  const visibleOptions = current.kind === "choice" && current.id === 95 && hiddenLetter
    ? (current.options || []).filter((o) => Q95_RELAX_LETTER[o] !== hiddenLetter)
    : current.options;

  const setAnswer = (val) => {
    if (isLikert) {
      setLikertAnswers((s) => ({ ...s, [current.id]: val }));
      return;
    }
    setExtraAnswers((s) => {
      const next = { ...s, [current.id]: val };
      if (current.id === 94) {
        const newLetter = Q94_REWARD_LETTER[val];
        const prev95 = s[95];
        if (newLetter && prev95 && Q95_RELAX_LETTER[prev95] === newLetter) {
          delete next[95];
        }
      }
      return next;
    });
  };

  const next = () => { if (index < total - 1) setIndex(index + 1); };
  const prev = () => { if (index > 0) setIndex(index - 1); };

  const submit = async () => {
    setSubmitting(true);
    // Cancel any pending auto-save so it can't restore a draft after the
    // server has already deleted it during /survey/submit.
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    pendingPayload.current = null;
    try {
      const { data: res } = await api.post("/survey/submit", {
        likert_answers: likertAnswers,
        extra_answers: extraAnswers,
      });
      setResult(res);
      try {
        const { data: list } = await api.get("/coachees");
        const own = Array.isArray(list) ? list[0] : null;
        if (own?.id) {
          const { data: c } = await api.get(`/coachees/${own.id}/job-coach`);
          if (c?.calendly_embed) setCalendlyEmbed(c.calendly_embed);
        }
      } catch (err) {
        console.warn("Calendly embed fetch failed (non-fatal):", err?.message || err);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const answeredCount = Object.keys(likertAnswers).length + Object.keys(extraAnswers).length;
  const canSubmit = answeredCount >= total * 0.9;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase size={22} weight="duotone" color="#1E88E5" />
          <div className="font-display text-sm font-bold tracking-tight">Breintype-test</div>
        </div>
        <div className="flex items-center gap-4">
          <SaveIndicator state={saveState} />
          <button onClick={handleExit} data-testid="survey-exit" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900">Sluiten</button>
        </div>
      </header>

      <div className="h-1 bg-zinc-100">
        <div className="h-full bg-zinc-900 transition-all duration-300" style={{ width: `${progress}%` }} data-testid="survey-progress" />
      </div>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl fade-up" key={current.id}>
          {restored && (
            <div
              className="mb-6 border-l-4 border-[#1E88E5] bg-[#1E88E5]/5 px-4 py-3 rounded-r-sm text-sm text-zinc-700 flex items-start justify-between gap-3"
              data-testid="survey-resumed-banner"
            >
              <div>
                <strong className="font-semibold">Voortgang hersteld.</strong>{" "}
                Je hervat de test waar je was gebleven. Je antwoorden worden automatisch opgeslagen.
              </div>
              <button
                type="button"
                onClick={() => setRestored(false)}
                data-testid="survey-resumed-dismiss"
                className="shrink-0 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900"
              >
                Sluit melding
              </button>
            </div>
          )}
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
            <span>{index + 1} / {total}</span>
            <span className="text-zinc-300">·</span>
            <span data-testid="survey-category">{current.category}</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-snug mb-8" data-testid="survey-question">
            {current.text}
          </h2>

          {isLikert ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {data.likert_options.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setAnswer(opt.value)}
                  data-testid={`likert-option-${opt.value}`}
                  className={
                    "btn-likert text-left p-5 border-2 rounded-sm transition-all " +
                    (currentAnswer === opt.value
                      ? opt.value === 1
                        ? "border-[#43A047] bg-[#43A047]/5"
                        : opt.value === -1
                        ? "border-[#E53935] bg-[#E53935]/5"
                        : "border-[#1E88E5] bg-[#1E88E5]/5"
                      : "border-zinc-200 hover:border-zinc-400 bg-white")
                  }
                >
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                    {opt.value === 1 ? "+1" : opt.value === -1 ? "−1" : "0"}
                  </div>
                  <div className="font-display text-base font-bold tracking-tight">{opt.label}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {visibleOptions.map((o) => (
                <button
                  type="button"
                  key={o}
                  onClick={() => setAnswer(o)}
                  data-testid={`choice-option-${o.substring(0, 20)}`}
                  className={
                    "block w-full text-left p-4 border-2 rounded-sm transition-all " +
                    (currentAnswer === o
                      ? "border-[#1E88E5] bg-[#1E88E5]/5"
                      : "border-zinc-200 hover:border-zinc-400 bg-white")
                  }
                >
                  <span className="font-medium text-[15px]">{o}</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-10">
            <button onClick={prev} disabled={index === 0} data-testid="survey-prev" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 disabled:opacity-30">
              <ArrowLeft size={16} /> Vorige
            </button>
            {index < total - 1 ? (
              <button onClick={next} disabled={currentAnswer === undefined} data-testid="survey-next" className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:opacity-40">
                Volgende <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !canSubmit} data-testid="survey-submit" className="inline-flex items-center gap-2 bg-[#43A047] text-white px-6 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#388e3c] transition-colors disabled:opacity-40">
                {submitting ? "Versturen..." : "Voltooien"} <CheckCircle size={16} />
              </button>
            )}
          </div>
          <div className="mt-4 text-center text-xs text-zinc-400" data-testid="survey-answered-count">
            {answeredCount} van {total} vragen beantwoord
          </div>
        </div>
      </main>
    </div>
  );
}

function CenterMessage({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-zinc-500">{children}</div>;
}

function SaveIndicator({ state }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400" data-testid="survey-saving">
        <FloppyDisk size={14} weight="duotone" />
        Opslaan...
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#43A047]" data-testid="survey-saved">
        <CheckCircle size={14} weight="bold" />
        Opgeslagen
      </span>
    );
  }
  return null;
}

function ResultScreen({ result, calendlyEmbed, onClose }) {
  const hasProbs = result.probabilities && Object.keys(result.probabilities).length > 0;
  return (
    <div className="min-h-screen bg-white py-12 px-6 flex justify-center">
      <div className="w-full max-w-3xl fade-up" data-testid="survey-result">
        <div className="text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center bg-[#43A047] text-white rounded-full mb-6">
            <CheckCircle size={32} weight="bold" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Test voltooid</h1>
          <p className="text-zinc-600 mt-2">
            Bedankt voor je antwoorden. Hieronder zie je de voorlopige uitslag — je work coach
            beoordeelt het definitieve breintype.
          </p>
          {hasProbs && result.top_brain_type_id && (
            <div className="mt-6 inline-flex items-center gap-3 border-l-4 border-[#1E88E5] bg-[#1E88E5]/5 px-4 py-3 rounded-r-sm" data-testid="survey-result-top">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Meest waarschijnlijke breintype</span>
              <span className="font-display text-base font-bold tracking-tight">
                #{result.top_brain_type_id} {formatBrainTypeLabel(result.top_brain_type_id)}
              </span>
            </div>
          )}
        </div>

        {hasProbs && (
          <div className="mt-8 text-left">
            <BrainTypeProbabilities probabilities={result.probabilities} topId={result.top_brain_type_id} />
          </div>
        )}

        <div className="text-center">
          <button onClick={onClose} data-testid="survey-result-close" className="mt-8 bg-zinc-900 text-white px-6 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
            Terug naar dashboard
          </button>
        </div>

        {calendlyEmbed && (
          <div className="mt-12 neuro-card p-6 md:p-8" data-testid="survey-result-calendly">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Volgende stap</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
              Plan een gesprek met je work coach
            </h2>
            <p className="text-sm text-zinc-600 mt-2">
              In een korte sessie bespreekt je work coach de uitslag met je en valideert het definitieve breintype.
            </p>
            <div className="mt-6">
              <CalendlyEmbed html={calendlyEmbed} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
